/**
 * AppLayout (B10) — the shells: desktop / compact / large / kiosk, and the mobile
 * bottom sheet (§A). Publishes the layout, kiosk flag, reduced motion and the
 * camera's safe insets to the store.
 *
 *   <AppLayout map={<MapStage/>} panel={…} card={…} hud={<Hud/>} overlays={…}/>
 *
 * Root: <div class="ld ld-loc-layout"> full viewport, with
 *   data-layout   desktop | mobile | kiosk        (store.layout)
 *   data-variant  mobile | compact | desktop | large | kiosk   (§A.1)
 *   data-kiosk    true | false                     (and --ld-k: 1.2 in kiosk)
 *   data-motion   reduced | full
 *   data-phase    loading | intro | explore | drive | attract
 *   data-ui       hidden | arriving | shown        (chrome arrival, §E.7)
 *   data-card     open | closed
 *   data-panel    yield                            narrow desktop with a place selected: the side panel
 *                                                  steps aside (panel.css); its slot goes inert once focus is out
 *   data-sheet    peek | half | full               (mobile)
 *   --ld-sheet-h      mobile: visible sheet height in px incl. the safe area. On the
 *                     hud slot it follows the sheet every frame (map buttons and
 *                     attribution sit at bottom: calc(var(--ld-sheet-h) + 12px), §A.4);
 *                     everywhere else (overlays: toasts, drive HUD) it is the settled
 *                     value from the root, so glass overlays never repaint per frame.
 *   --ld-sheet-cover  mobile: 0 at half or lower → 1 at full, same cadence. Riding
 *                     chrome fades with opacity: calc(1 - var(--ld-sheet-cover)) and
 *                     stops taking pointers at [data-sheet="full"] (at full the map is
 *                     covered and the attribution lives at the end of the list, §A.4).
 *
 * Slots ([data-ld-slot], `display: contents`, so children position against the root):
 *   desktop/kiosk  overlays · panel · card · hud · map · hud-end   (DOM order = focus order §H.1)
 *   mobile         overlays · sheet(panel | card) · hud · map · hud-end
 * hud-end holds what §H.1 puts after the map region (the attribution; the HUD portals it there).
 * While the loader covers the app (phase "loading") every slot is inert: nothing behind it can be
 * reached by keyboard or read out (the live regions stay outside the slots).
 * On mobile the integrator passes the embedded variants:
 *   panel={<PlacesPanel embedded/>} card={<PlaceCard embedded … />}
 * The card layer is shown while a place is selected (and not driving); the panel
 * layer stays mounted underneath.
 *
 * Chrome hooks for the other modules (see chrome.ts):
 *   .ld-ui-in / data-ld-chrome="self"  a child that runs its own arrival and drive
 *                         state (B7's panel and B9's hud do); the layout leaves it alone
 *   data-ld-drive="live"  an unmanaged hud child that stays live in a drive
 *   data-ld-drive="dim"   inside a live child: dims anyway
 *   data-ld-drive-focus   "End the drive" (focus lands there when a drive starts)
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import type { AppLayoutProps } from "@/contracts/ui";
import type { SheetSnap } from "@/contracts/store";
import { useLightField, useReducedMotion } from "@/design";
import { actions, appStore, getApp, useApp } from "@/store/store";
import { Announcer } from "@/features/a11y/Announcer";
import type { AnnounceMoment } from "@/features/a11y/useAnnouncements";
import { SkipLink } from "@/features/a11y/SkipLink";
import { CARD_SELECTOR, focusLost, focusSafely, useFocusReturn } from "@/features/a11y/focus";
import { useGlobalKeys, type GlobalKeysOptions } from "@/features/a11y/useGlobalKeys";
import { useReducedMotionSync } from "@/features/a11y/useReducedMotionSync";
import { chromeState } from "./arrival";
import { BottomSheet } from "./BottomSheet";
import { useChromeMotion } from "./chrome";
import { sheetHeights } from "./layoutMath";
import { applyRidgeBlob } from "./ridgeBlob";
import { panelAside, useLayout } from "./useLayout";
import { useUiArrival } from "./useUiArrival";
import "./layout.css";

export interface AppLayoutExtras {
  /** Global Escape handling (useGlobalKeys). false turns it off; an object passes callbacks. */
  keys?: GlobalKeysOptions | false;
  /** Mount the live regions + §H.3 announcements. Default true (a second <Announcer/> elsewhere is harmless). */
  announcer?: boolean;
  /** Announcement moments that other modules speak themselves (see useAnnouncements). */
  announceSkip?: readonly AnnounceMoment[];
  /** id of the places listbox the skip link targets. Default "ld-places" (B7). */
  skipTarget?: string;
}

/**
 * Mobile: a newly selected place opens the card at half (§A.4); closing it
 * restores the snap the list had.
 */
function useSheetSelectionSnaps(mobile: boolean): void {
  useEffect(() => {
    if (!mobile) return undefined;
    let listSnap: SheetSnap | null = null;
    return appStore.subscribe((s, p) => {
      if (s.phase === "attract" || s.selectedId === p.selectedId) return;
      if (s.selectedId && !p.selectedId) {
        listSnap = s.sheet;
        if (s.sheet !== "half") actions.setSheet("half");
      } else if (!s.selectedId && p.selectedId) {
        if (listSnap && listSnap !== s.sheet) actions.setSheet(listSnap);
        listSnap = null;
      }
    });
  }, [mobile]);
}

/** The panel's zone in either shell (the side panel's slot, or the sheet's list layer). */
const PANEL_ZONE = '[data-ld-slot="panel"], .ld-loc-layout-sheet-layer--panel';

/**
 * Reflow keeps the user's place (WCAG 1.4.10, 2.4.3): switching between the side panel and the sheet
 * remounts the panel and the card, so focus held there would fall to <body>. It returns to the same
 * element (by id) or its zone's anchor: the listbox, or the card heading. In the sheet, a list that
 * takes focus at peek rises to half so it can be seen.
 */
function useFocusAcrossLayouts(rootRef: RefObject<HTMLDivElement | null>, mobile: boolean, listId: string): void {
  const last = useRef<{ id: string; zone: "panel" | "card" } | null>(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const onIn = (e: FocusEvent) => {
      const t = e.target instanceof HTMLElement ? e.target : null;
      if (!t) return;
      const zone = t.closest(PANEL_ZONE) ? "panel" : t.closest(CARD_SELECTOR) ? "card" : null;
      last.current = zone ? { id: t.id, zone } : null;
    };
    root.addEventListener("focusin", onIn);
    return () => root.removeEventListener("focusin", onIn);
  }, [rootRef]);
  const was = useRef(mobile);
  useLayoutEffect(() => {
    if (was.current === mobile) return undefined;
    was.current = mobile;
    const held = last.current;
    if (!held || !focusLost()) return undefined;
    const start = performance.now();
    let t = 0;
    // once the sheet has risen (640 settle), the active option comes into view below any stuck header
    const showActive = () => {
      const box = document.activeElement;
      const opt = box?.id === listId ? box.getAttribute("aria-activedescendant") : null;
      if (opt) document.getElementById(opt)?.scrollIntoView({ block: "nearest" });
    };
    const step = () => {
      const byId = held.id ? document.getElementById(held.id) : null;
      const anchor = held.zone === "panel" ? document.getElementById(listId) : document.getElementById("ld-card-title");
      if (focusSafely(byId) || focusSafely(anchor)) {
        if (mobile && held.zone === "panel" && getApp().sheet === "peek") {
          actions.setSheet("half");
          t = window.setTimeout(showActive, 700);
        }
        return;
      }
      if (performance.now() - start < 1500) t = window.setTimeout(step, 60);
    };
    t = window.setTimeout(step, 0);
    return () => window.clearTimeout(t);
  }, [mobile, listId]);
}

/**
 * The side panel stepping aside (narrow desktop, a card open): its slot goes inert once focus is out of
 * it. Focus held in the list (Enter keeps it there, §B.3) moves to the card heading when the card is in.
 */
function usePanelAside(slotRef: RefObject<HTMLDivElement | null>, aside: boolean): boolean {
  const [inert, setInert] = useState(false);
  useEffect(() => {
    if (!aside) {
      setInert(false);
      return undefined;
    }
    const holds = () => {
      const a = document.activeElement;
      return !!a && a !== document.body && !!slotRef.current?.contains(a);
    };
    if (!holds()) {
      setInert(true);
      return undefined;
    }
    const start = performance.now();
    let t = 0;
    const step = () => {
      if (!holds() || focusSafely(document.getElementById("ld-card-title")) || performance.now() - start > 8000) {
        setInert(true);
        return;
      }
      t = window.setTimeout(step, 100);
    };
    t = window.setTimeout(step, 100);
    return () => window.clearTimeout(t);
  }, [aside, slotRef]);
  return aside && inert;
}

export function AppLayout({ map, panel, card, hud, overlays, keys, announcer = true, announceSkip, skipTarget }: AppLayoutProps & AppLayoutExtras) {
  const rootRef = useRef<HTMLDivElement>(null);
  const hudRef = useRef<HTMLDivElement>(null);
  const panelSlotRef = useRef<HTMLDivElement>(null);

  const info = useLayout();
  useReducedMotionSync();
  const reduced = useReducedMotion();
  const phase = useApp((s) => s.phase);
  const selected = useApp((s) => s.selectedId !== null);
  const sheet = useApp((s) => s.sheet);
  const { ui, stagger } = useUiArrival(phase, reduced);
  const chrome = chromeState(phase);
  const mobile = info.variant === "mobile";
  const driving = phase === "drive";
  const loading = phase === "loading";
  const aside = useApp((s) => !mobile && panelAside(s, info));
  const panelInert = usePanelAside(panelSlotRef, aside);
  useFocusAcrossLayouts(rootRef, mobile, skipTarget ?? "ld-places");

  useGlobalKeys(keys === false ? { enabled: false } : (keys ?? {}));
  useFocusReturn();
  // One raking-light field for the whole app (B7, B8 and B9 rely on the root's; a second would ripple twice).
  useLightField(rootRef);
  // The ridge texture as a short blob: URL for everything in the app (a 215 KB data: URL is re-parsed in
  // every raking-light style recalculation; see ridgeBlob.ts).
  useEffect(() => (rootRef.current ? applyRidgeBlob(rootRef.current) : undefined), []);
  useSheetSelectionSnaps(mobile);
  useChromeMotion(rootRef, { ui, stagger, chrome, reduced, variant: info.variant });

  // Chrome that rides on the sheet reads --ld-sheet-h; per frame only on the hud slot,
  // and only when the rounded value changes.
  const last = useRef({ h: "", c: "" });
  const onSheetHeight = useCallback((px: number, cover: number) => {
    const el = hudRef.current;
    if (!el) return;
    const h = `${Math.round(px)}px`;
    const c = String(Math.round(cover * 100) / 100);
    if (h !== last.current.h) el.style.setProperty("--ld-sheet-h", h);
    if (c !== last.current.c) el.style.setProperty("--ld-sheet-cover", c);
    last.current = { h, c };
  }, []);
  const cardMode = selected && !driving;
  // Settled visible height (the sheet steps aside in a drive).
  const restH = mobile && !driving ? sheetHeights(info.vh, cardMode ? "card" : "panel", info.safe)[sheet] + info.safe.bottom : 0;
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (mobile) {
      root.style.setProperty("--ld-sheet-h", `${restH}px`);
      root.style.setProperty("--ld-sheet-cover", sheet === "full" && !driving ? "1" : "0");
    } else {
      last.current = { h: "", c: "" };
      for (const el of [root, hudRef.current]) {
        el?.style.removeProperty("--ld-sheet-h");
        el?.style.removeProperty("--ld-sheet-cover");
      }
    }
  }, [mobile, restH, sheet, driving]);

  const rootStyle = { "--ld-k": info.metrics.k } as CSSProperties;

  return (
    <div
      ref={rootRef}
      className="ld ld-loc-layout"
      data-layout={info.metrics.layout}
      data-variant={info.variant}
      data-kiosk={info.kiosk ? "true" : "false"}
      data-motion={reduced ? "reduced" : "full"}
      data-phase={phase}
      data-ui={ui}
      data-card={selected ? "open" : "closed"}
      data-panel={aside ? "yield" : undefined}
      data-sheet={mobile ? sheet : undefined}
      style={rootStyle}
    >
      <SkipLink targetId={skipTarget} hidden={ui === "hidden" || info.kiosk} />

      <div className="ld-loc-layout-slot ld-loc-layout-slot--overlays" data-ld-slot="overlays" inert={loading || undefined}>
        {overlays}
      </div>

      {mobile ? (
        <div className="ld-loc-layout-slot ld-loc-layout-slot--sheet" data-ld-slot="sheet" inert={loading || undefined}>
          <BottomSheet
            panel={panel}
            card={card}
            mode={cardMode ? "card" : "panel"}
            snap={sheet}
            onSnap={actions.setSheet}
            vh={info.vh}
            safeTop={info.safe.top}
            safeBottom={info.safe.bottom}
            ui={ui}
            driving={driving}
            reduced={reduced}
            onHeight={onSheetHeight}
          />
        </div>
      ) : (
        <>
          {/* §E.6: in a drive the panel is inert (it dims from data-phase, panel.css): one attribute, no re-render */}
          <div
            ref={panelSlotRef}
            className="ld-loc-layout-slot ld-loc-layout-slot--panel"
            data-ld-slot="panel"
            inert={loading || driving || panelInert || undefined}
          >
            {panel}
          </div>
          <div className="ld-loc-layout-slot ld-loc-layout-slot--card" data-ld-slot="card" data-ld-card-slot="" inert={loading || undefined}>
            {card}
          </div>
        </>
      )}

      <div ref={hudRef} className="ld-loc-layout-slot ld-loc-layout-slot--hud" data-ld-slot="hud" inert={loading || undefined}>
        {hud}
      </div>

      <div className="ld-loc-layout-map" data-ld-slot="map" tabIndex={-1} inert={loading || undefined}>
        {map}
      </div>

      {/* §H.1: after the map region (the HUD portals the attribution here) */}
      <div className="ld-loc-layout-slot ld-loc-layout-slot--hud" data-ld-slot="hud-end" inert={loading || undefined} />

      {announcer && <Announcer skip={announceSkip} />}
    </div>
  );
}
